import { useCallback, useMemo } from 'react';

import {
  SearchableVendorTaxonomyKey,
  VendorTaxonomyKey,
  isVendorTaxonomyKey as isSearchableVendorTaxonomyKey,
  BANDS_DJS_TAXONOMY_KEY,
  BAR_SERVICES_TAXONOMY_KEY,
  CAKES_DESSERTS_TAXONOMY_KEY,
  CATERING_TAXONOMY_KEY,
  FLORISTS_TAXONOMY_KEY,
  HAIR_MAKEUP_TAXONOMY_KEY,
  PHOTOGRAPHERS_TAXONOMY_KEY,
  PLANNERS_TAXONOMY_KEY,
  VENUES_TAXONOMY_KEY,
  VIDEOGRAPHERS_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorTaxonomySwitchFunc } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import { COLORS3 } from '@zola/zola-ui/src/styles/emotion';
import MEDIA_QUERY_V2 from '@zola/zola-ui/src/styles/emotion/mediaQueryV2';
import H from '@zola/zola-ui/src/typography/Headings';

import styled from '@emotion/styled';

import { LocationOrAllMarketsType } from '~/types/responseTypes';
import { ALL_MARKETS, isSearchLocation } from '~/util/searchUtils';

import { PageSection } from '../../../PageSection/PageSection';
import SaveNewSearchButton from '../../../SavedSearches/SaveNewSearchButton';
import { InlineSearchBar } from '../../../SearchBar/InlineSearchBar';
import { useSearchResultsPageContext } from '../../contexts/SearchResultsPageContext';
import { useVendorSearchNavigation } from '../../contexts/VendorSearchNavigationContext';
import { PartialTaxonomyNode } from '../../types/types';
import { getCanonicalFacetDescription } from '../../utils/canonicalFacetHelpers';
import { FeaturedVendorsToolTip } from '../FeaturedVendorsToolTip/FeaturedVendorsToolTip';
import { MobileSearchDropdownsPlaceholder } from '../SearchControls/MobileSearchDropdownsPlaceholder';

const Wrapper = styled(PageSection)`
  padding-top: 16px;

  .react-autosuggest__suggestions-container.react-autosuggest__suggestions-container--open {
    // Override a 30px right indent on the dropdown
    width: 100%;

    // Set a smaller font-size
    font-size: 14px;
  }
`;

const Content = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;

  ${MEDIA_QUERY_V2.EXCLUDE_DESKTOP} {
    flex-direction: column-reverse;
  } ;
`;

const categoryHeadings = getVendorTaxonomySwitchFunc<SearchableVendorTaxonomyKey, string>({
  [BANDS_DJS_TAXONOMY_KEY]: 'Bands and DJs',
  [BAR_SERVICES_TAXONOMY_KEY]: 'Bar Services & Beverages',
  [CAKES_DESSERTS_TAXONOMY_KEY]: 'Cakes & Desserts',
  [CATERING_TAXONOMY_KEY]: 'Caterers',
  [FLORISTS_TAXONOMY_KEY]: 'Florists',
  [HAIR_MAKEUP_TAXONOMY_KEY]: 'Beauty Professionals',
  [PHOTOGRAPHERS_TAXONOMY_KEY]: 'Photographers',
  [PLANNERS_TAXONOMY_KEY]: 'Planners',
  [VENUES_TAXONOMY_KEY]: 'Venues',
  [VIDEOGRAPHERS_TAXONOMY_KEY]: 'Videographers',
});

export const getCategoryHeading = (selectedTaxonomy: VendorTaxonomyKey | undefined) => {
  if (isSearchableVendorTaxonomyKey(selectedTaxonomy)) {
    return categoryHeadings(selectedTaxonomy);
  }
  return 'Vendors';
};

const Hed = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${MEDIA_QUERY_V2.EXCLUDE_DESKTOP} {
    gap: 4px;
  }
`;

const Title = () => {
  const { vendorSearch } = useSearchResultsPageContext();
  const { selectedTaxonomy, selectedLocation, selectedFacets } = vendorSearch;

  const formattedTitle = useMemo(() => {
    const facetDescription =
      getCanonicalFacetDescription(selectedTaxonomy?.key, selectedFacets, true) || '';
    const description = `Wedding ${getCategoryHeading(selectedTaxonomy?.key)}`;
    const location = isSearchLocation(selectedLocation) ? selectedLocation.label : undefined;
    let title = `${facetDescription} ${description}`.trim();
    if (location) {
      title = `${title} in ${location}`;
    }
    return title;
  }, [selectedFacets, selectedLocation, selectedTaxonomy]);

  return (
    <H.Title1 serif presentation="h4">
      {formattedTitle}
    </H.Title1>
  );
};

const SearchBarWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;

  ${MEDIA_QUERY_V2.DESKTOP} {
    flex-grow: 1;

    max-width: 800px;
    @media (max-width: 1600px) {
      max-width: 50%;
    }
  }
`;

const StyledSaveSearch = styled(SaveNewSearchButton)`
  color: ${COLORS3.BLACK_075};
  font-weight: 400;
  font-size: 14px;
`;

const DesktopSearchBar = styled(InlineSearchBar)`
  padding: 0;
  gap: 8px;

  .marketplace-category-select {
    .marketplace-autosuggest-input {
      // override 12px padding on the auto suggest input
      padding-right: 0;
    }
    // For the panel of the dropdown to be of a minimum width of 260px
    .react-autosuggest__suggestions-container.react-autosuggest__suggestions-container--open {
      min-width: 260px;
    }
  }
  .marketplace-location-select {
    .marketplace-autosuggest-input {
      // override 12px padding on the auto suggest input
      padding-left: 0;
    }

    // For the panel of the dropdown to be of a minimum width of 260px
    // And since its on the right, make sure the panel is right aligned
    .react-autosuggest__suggestions-container.react-autosuggest__suggestions-container--open {
      min-width: 260px;
      right: 0;
    }
  }

  ${MEDIA_QUERY_V2.EXCLUDE_DESKTOP} {
    display: none;
  }
`;

const MobileSearchBar = styled.div`
  width: 100%;
  display: none;
  ${MEDIA_QUERY_V2.EXCLUDE_DESKTOP} {
    display: block;
  }
`;

const SavedSearch = () => {
  const { vendorSearch, selectedFacetOptions } = useSearchResultsPageContext();
  const { availabilityDates } = vendorSearch;

  return (
    <StyledSaveSearch
      vendorSearch={vendorSearch}
      selectedFacetOptions={selectedFacetOptions}
      availabilityDates={availabilityDates}
      component="link"
    />
  );
};
export const PageHeading = () => {
  const { vendorSearch } = useSearchResultsPageContext();
  const { selectedLocation, selectedTaxonomy } = vendorSearch;

  const { navigateToSrp } = useVendorSearchNavigation();

  // Navigate on change if the location is cleared
  const updateSelectedLocation = useCallback(
    (newLocation: LocationOrAllMarketsType) => {
      navigateToSrp({
        selectedLocation: newLocation,
      });
    },
    [navigateToSrp]
  );

  // Navigate on change if the category is cleared
  const updateTaxonomy = useCallback(
    (newCategory: PartialTaxonomyNode | null) => {
      navigateToSrp({
        selectedTaxonomy: newCategory,
      });
    },
    [navigateToSrp]
  );

  return (
    <Wrapper noPadding>
      <Content>
        <Hed>
          <Title />
          <FeaturedVendorsToolTip />
        </Hed>
        <SearchBarWrapper>
          <MobileSearchBar>
            <MobileSearchDropdownsPlaceholder />
          </MobileSearchBar>

          <DesktopSearchBar
            selectedLocation={selectedLocation || ALL_MARKETS}
            allowInteractiveSearch
            updateSelectedLocation={updateSelectedLocation}
            selectedTaxonomyNode={selectedTaxonomy ?? null}
            setSelectedTaxonomyNode={updateTaxonomy}
            navigateToSrp={navigateToSrp}
            variant="white"
            reverse
            short
            searchIcon
          />

          <SavedSearch />
        </SearchBarWrapper>
      </Content>
    </Wrapper>
  );
};
